import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchAllBlogs } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const BlogSection = () => {
  const { language } = useLanguage();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLatestBlogs = async () => {
      try {
        setLoading(true);
        const blogsData = await fetchAllBlogs();
        setBlogs(blogsData.slice(0, 3));
      } catch (err) {
        console.error('Error loading blogs:', err);
        setError(language === 'MR' ? 'अनुदिनी लोड करण्यात अयशस्वी' : 'Failed to load blogs');
      } finally {
        setLoading(false);
      }
    };

    loadLatestBlogs();
  }, [language]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options).toUpperCase();
  };

  const getImageSrc = (thumbnail) => {
    if (!thumbnail) return '/api/placeholder/600/400';
    if (thumbnail.startsWith('data:')) return thumbnail;
    if (thumbnail.startsWith('http')) return thumbnail;
    if (thumbnail.startsWith('uploads/')) {
      return `${process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500'}/${thumbnail}`;
    }
    return '/api/placeholder/600/400';
  };

  // Helper to safely extract content preview
  const getContentPreview = (content, length = 150) => {
    if (!content) return '';
    try {
      // Handle both string and rich text content
      const text = typeof content === 'string' 
        ? content 
        : content.blocks?.map(block => block.text).join(' ') || '';
      
      return text
        .replace(/<[^>]*>/g, ' ') // Remove HTML tags
        .replace(/\s+/g, ' ')      // Collapse whitespace
        .substring(0, length) 
        + '...';
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center mb-12">
            <div className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red"></div>
            <div className="text-center">
              <h2 className="text-4xl font-bold text-red-800 mr-8">
                {language === 'MR' ? 'शब्दांच्या पंखांवर' : 'WHISPERS OF INK'}
              </h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'व्यक्तिगत अनुभव आणि जीवनकथा' : 'Personal experiences and life stories'}
              </p>
            </div>
            <div className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"></div>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="bg-gray-300 rounded-xl mb-4 h-[320px]"></div>
                  <div className="h-6 bg-gray-300 mb-2 w-2/3"></div>
                  <div className="h-4 bg-gray-300 w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center mb-10">
            <div className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red"></div>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-red-800 mr-6">
                {language === 'MR' ? 'शब्दांच्या पंखांवर' : 'WHISPERS OF INK'}
              </h2>
              <p className="text-sm text-gray-600 mt-2 italic">
                {language === 'MR' ? 'व्यक्तिगत अनुभव आणि जीवनकथा' : 'Personal experiences and life stories'}
              </p>
            </div>
            <div className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"></div>
          </div>
          <div className="bg-red-50 border-2 border-red-100 rounded-xl p-6 max-w-md mx-auto">
            <div className="text-4xl text-red-800 mb-3">⚠</div>
            <p className="text-black font-semibold text-sm">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center mb-10">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-transparent to-red-800"
          />
          <div className="text-center">
            <motion.h2 
              className="text-3xl font-bold text-red-800 mr-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {language === 'MR' ? 'शब्दांच्या पंखांवर' : 'WHISPERS OF INK'}
            </motion.h2>
            <p className="text-sm text-gray-600 mt-2 italic">
              {language === 'MR' ? 'व्यक्तिगत अनुभव आणि जीवनकथा' : 'Personal experiences and life stories'}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 h-0.5 bg-gradient-to-r from-red-800 to-transparent"
          />
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {blogs.map((blog, index) => (
              <motion.div
                key={blog._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="group cursor-pointer"
              >
                <div className="flip-card h-[320px]">
                  <div className="flip-card-inner">
                    {/* Front Side */}
                    <div className="flip-card-front relative overflow-hidden rounded-xl bg-gray-200 h-full">
                      <img loading="lazy" 
                        src={getImageSrc(blog.thumbnail)}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-all duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="bg-white/90 text-black px-2 py-1 rounded-full text-xs font-bold tracking-wider">
                            {blog.tags && blog.tags.length > 0 ? blog.tags[0].toUpperCase() : 'TECHNOLOGY'}
                          </span>
                          <span className="text-white/90 text-xs font-medium">
                            {formatDate(blog.date)}
                          </span>
                        </div>
                        
                        <h3 className="text-base font-bold text-white line-clamp-2">
                          {blog.title}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Back Side - Updated to white background with black text */}
                    <div className="flip-card-back relative overflow-hidden rounded-xl bg-white p-5 flex flex-col justify-center h-full">
                      <div className="relative z-10 text-black">
                        <div className="flex justify-between items-center mb-3">
                          <span className="bg-gray-100 text-black px-2 py-1 rounded-full text-xs font-bold tracking-wider">
                            {blog.tags && blog.tags.length > 0 ? blog.tags[0].toUpperCase() : 'TECHNOLOGY'}
                          </span>
                          <span className="text-gray-600 text-xs font-medium">
                            {formatDate(blog.date)}
                          </span>
                        </div>
                        
                        <h3 className="text-base font-bold mb-3 line-clamp-2">
                          {blog.title}
                        </h3>
                        
                        <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                          {getContentPreview(blog.content, 120)}
                        </p>
                        
                        <Link 
                          to={`/blog/${blog.slug || blog._id}`}
                          className="inline-flex items-center text-red-800 group-hover:text-red-600 transition-colors"
                        >
                          <span className="font-semibold text-sm mr-1">
                            {language === 'MR' ? 'अधिक वाचा' : 'READ MORE'}
                          </span>
                          <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Flip card CSS */}
      <style jsx>{`
        .flip-card {
          perspective: 1200px;
          width: 100%;
          position: relative;
          border-radius: 0.75rem;
          overflow: hidden;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }
        
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
          will-change: transform;
        }
        
        .flip-card:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        
        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        
        .flip-card-back {
          transform: rotateY(180deg);
        }
      `}</style>
    </section>
  );
};

export default BlogSection;